// ABOUTME: Email verification page component where users verify their email using the token from email
// ABOUTME: Handles token verification with appropriate success/error messages and resend functionality

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  TextField
} from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { verifyEmail, resendVerificationEmail } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // Get token from URL parameters
  const token = searchParams.get('token');

  // Verify email on component mount
  useEffect(() => {
    const performVerification = async () => {
      if (!token) {
        setError('Invalid verification link. No token provided.');
        setIsVerifying(false);
        return;
      }

      try {
        await verifyEmail(token);
        setIsSuccess(true);
        try {
          await refreshUser();
        } catch {
          // refreshUser failure is non-blocking — user is verified, continue to dashboard
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
        if (axiosErr.response?.status === 400) {
          setError(axiosErr.response?.data?.message || 'Invalid or expired verification token');
        } else {
          setError('An error occurred during verification. Please try again.');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    performVerification();
  }, [token, refreshUser]);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resendEmail || !resendEmail.includes('@')) {
      setResendMessage('Please enter a valid email address');
      return;
    }

    setIsResending(true);
    setResendMessage('');

    try {
      const result = await resendVerificationEmail(resendEmail);
      setResendMessage(result.message || 'Verification email sent! Please check your inbox.');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 429) {
        setResendMessage(axiosErr.response?.data?.message || 'You can only send one email every 5 minutes. Please try again later.');
      } else {
        setResendMessage('Failed to send verification email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          py: 3,
          background: theme => theme.palette.gradient.secondary,
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center">
            <CircularProgress size={64} sx={{ mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Verifying Your Email...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show success state
  if (isSuccess) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          py: 3,
          background: theme => theme.palette.gradient.secondary,
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                mx: 'auto',
                mb: 2
              }}
            >
              <MarkEmailReadIcon fontSize="large" />
            </Avatar>

            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Email Verified Successfully
            </Typography>

            <Card sx={{ mt: 3, mb: 4 }}>
              <CardContent>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                  Your email address has been successfully verified.
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {user
                    ? 'You can now access all features of your account.'
                    : 'You can now log in to access your account.'}
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(user ? '/' : '/login')}
              fullWidth
            >
              {user ? 'Go to Dashboard' : 'Go to Login'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show error state with resend option
  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 3,
        background: theme => theme.palette.gradient.secondary,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
        <Box textAlign="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'error.main',
              color: 'error.contrastText',
              mx: 'auto',
              mb: 2
            }}
          >
            <ErrorIcon fontSize="large" />
          </Avatar>

          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Verification Failed
          </Typography>

          <Alert severity="error" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Need a new verification link?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address below to receive a new verification link.
              </Typography>

              <Box component="form" onSubmit={handleResendVerification}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  variant="outlined"
                  margin="normal"
                  autoFocus
                />

                {resendMessage && (
                  <Alert
                    severity={resendMessage.includes('sent') ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                  >
                    {resendMessage}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isResending}
                  sx={{ mt: 2 }}
                >
                  {isResending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/login')}
            fullWidth
          >
            Back to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default VerifyEmail;
